import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  FormControl,
} from "@mui/material";
import adminServices from "../../services/adminServices";

export interface UserOption {
  id: string;
  name: string;
}

interface AutoCompleteUserSelectProps {
  label?: string;
  placeholder?: string;
  value: UserOption | null;
  onChange: (selected: UserOption | null) => void;
  error?: string;
  required?: boolean;

  backgroundColor?: string;
}

const AutoCompleteUserSelect: React.FC<AutoCompleteUserSelectProps> = ({
  label = "Recommended By",
  placeholder = "Search by name",
  value,
  onChange,
  error,
  required = false,
  backgroundColor,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (value === null) {
      setInputValue("");
    }
  }, [value]);

  const fetchData = useCallback(async () => {
    if (inputValue.length > 1) {
      setLoading(true);
      try {
        const { data } = await adminServices.getSearchUser({
          searchTerm: inputValue,
        });
        if (data?.statusCode === 200 && data.data.length > 0) {
          const lowerInput = inputValue.toLowerCase().trim();

          const filtered = data.data.filter(
            (item: { fullName: string; userId: string }) =>
              item.fullName.toLowerCase().includes(lowerInput) ||
              item.userId.toLowerCase().includes(lowerInput)
          );

          const mapped: UserOption[] = filtered.map(
            (user: { userId: string; fullName: string }) => ({
              id: user.userId,
              name: `${user.fullName} | ${user.userId}`,
            })
          );

          setOptions(mapped);
        } else {
          setOptions([]);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    } else {
      setOptions([]);
    }
  }, [inputValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Typography
          variant="subtitle1"
          sx={{ fontSize: "12px", fontWeight: 500, color: "#ffffff" }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}
      <FormControl fullWidth error={!!error}>
        <Autocomplete
          freeSolo
          loading={loading}
          options={options}
          value={value}
          inputValue={inputValue}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name
          }
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          sx={{
            "& .MuiAutocomplete-clearIndicator": {
              color: "#ffffff",
            },
          }}
          onChange={(_, newValue) => {
            if (typeof newValue === "object" && newValue !== null) {
              onChange(newValue);
            } else {
              onChange(null);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              sx={{
                "& fieldset": {
                  borderColor: error ? "#d32f2f !important" : "#808080",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  padding: "2px",
                  fontSize: "14px",
                  backgroundColor: backgroundColor || "#393939",
                  color: "#CCCCCC",
                  "& fieldset": { borderColor: "#808080" },
                  "&:hover fieldset": { borderColor: "#808080" },
                  "&.Mui-focused fieldset": { borderColor: "#808080" },
                  "& input::placeholder": {
                    color: "#CCCCCC",
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {error && (
          <Typography
            className="f-12"
            sx={{
              fontWeight: "medium",
              textAlign: "left",
              color: "#d32f2f",
              paddingTop: "0px",
            }}
          >
            {error}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};

export default AutoCompleteUserSelect;
